/**
 * Utility functions for matching CSV column names with field names
 */

type FieldMapping = {
  [key: string]: string[];
};

// Define the field mappings with their possible variations
const fieldMappings: FieldMapping = {
  firstName: [
    'first_name',
    'firstname',
    'first name',
    'fname',
    'first',
    'given name',
    'given_name',
    'givenname'
  ],
  lastName: [
    'last_name',
    'lastname',
    'last name',
    'lname',
    'last',
    'family name',
    'family_name',
    'familyname',
    'surname'
  ],
  phoneNumber: [
    'phone_number',
    'phonenumber',
    'phone number',
    'phone',
    'mobile',
    'mobile_number',
    'mobile number',
    'cell',
    'cell_number',
    'cell number',
    'telephone',
    'telephone_number',
    'contact',
    'contact_number',
    'contact number'
  ],
  email: [
    'email',
    'email_address',
    'email address',
    'e-mail',
    'e mail',
    'mail',
    'mail_address',
    'mail address',
    'contact_email',
    'contact email',
    'primary_email',
    'primary email'
  ]
};

/**
 * Normalizes a string by removing special characters and converting to lowercase
 */
const normalizeString = (str: string): string => {
  return str.toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Creates a regex pattern for a field name that matches various formats
 * @param fieldName - The field name to create a pattern for
 * @returns A regex pattern that matches various formats of the field name
 */
const createFieldPattern = (fieldName: string): RegExp => {
  // Convert camelCase to words
  const words = fieldName
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
    .split(' ');

  // Create pattern parts
  const patternParts = words.map((word) => {
    // Match the word itself or common abbreviations
    if (word === 'first' || word === 'last') {
      return `(${word}|${word[0]})`;
    }
    return word;
  });

  // Create the full pattern with optional separators
  const basePattern = patternParts.join('[-_\\s]*');

  // Add common suffixes
  const suffixes = ['name', 'number', 'address', 'email', 'mail'];
  const suffixPattern = suffixes.some((suffix) =>
    fieldName.toLowerCase().includes(suffix)
  )
    ? ''
    : `(${suffixes.join('|')})?`;

  return new RegExp(`^${basePattern}${suffixPattern}$`, 'i');
};

/**
 * Checks if a CSV column name matches any of the defined field names
 * @param csvColumnName - The column name from the CSV
 * @param fieldName - The field name to match against
 * @returns boolean indicating if there's a match
 */
export const isColumnMatch = (
  csvColumnName: string,
  fieldName: string
): boolean => {
  const normalizedCsvColumn = normalizeString(csvColumnName);
  const normalizedFieldName = normalizeString(fieldName);

  // Direct match
  if (normalizedCsvColumn === normalizedFieldName) {
    return true;
  }

  // Check against predefined mappings
  const possibleMatches = fieldMappings[fieldName] || [];
  if (
    possibleMatches.some(
      (match) => normalizeString(match) === normalizedCsvColumn
    )
  ) {
    return true;
  }

  // Use regex pattern matching as a fallback
  const pattern = createFieldPattern(fieldName);
  return pattern.test(csvColumnName);
};

/**
 * Finds the matching field name for a given CSV column name
 * @param csvColumnName - The column name from the CSV
 * @returns The matching field name or null if no match is found
 */
export const findMatchingField = (csvColumnName: string): string | null => {
  const normalizedCsvColumn = normalizeString(csvColumnName);

  // First try exact matches and predefined mappings
  for (const [fieldName, variations] of Object.entries(fieldMappings)) {
    if (
      normalizedCsvColumn === normalizeString(fieldName) ||
      variations.some(
        (variation) => normalizeString(variation) === normalizedCsvColumn
      )
    ) {
      return fieldName;
    }
  }

  // Then try regex pattern matching
  for (const fieldName of Object.keys(fieldMappings)) {
    const pattern = createFieldPattern(fieldName);
    if (pattern.test(csvColumnName)) {
      return fieldName;
    }
  }

  return null;
};

/**
 * Maps CSV headers to field names
 * @param headers - Array of CSV column headers
 * @returns Object mapping CSV headers to field names
 */
export const csvColumnMatcher = (
  headers: string[]
): { [key: string]: string } => {
  const mapping: { [key: string]: string } = {};

  headers.forEach((header) => {
    const matchingField = findMatchingField(header);
    if (matchingField) {
      mapping[matchingField] = header;
    }
  });

  return mapping;
};

/**
 * Maps field names to CSV column names
 * @param headers - Array of CSV column headers
 * @returns Object mapping field names to CSV column names
 */
export const fieldToColumnMatcher = (
  headers: string[]
): { [key: string]: string | undefined } => {
  // Get the column to field mapping
  const columnToFieldMapping = csvColumnMatcher(headers);

  // Create a mapping from field names to column names
  const fieldToColumnMapping: { [key: string]: string | undefined } = {};

  // Initialize all fields as undefined
  Object.keys(fieldMappings).forEach((fieldName) => {
    fieldToColumnMapping[fieldName] = undefined;
  });

  // Fill in the mapping with matches
  Object.entries(columnToFieldMapping).forEach(([columnName, fieldName]) => {
    fieldToColumnMapping[fieldName] = columnName;
  });

  return fieldToColumnMapping;
};
